/**
 * Neumorphism Theme Styles
 * 
 * Estilos específicos para o tema Neumorfismo (Soft UI).
 * Características: sombras suaves, efeito tátil, bordas arredondadas.
 */

import { CSSProperties } from 'react';

// ============================================
// BASE STYLES
// ============================================

export const neumorphismBase: CSSProperties = {
  background: 'var(--neu-bg)',
  color: 'var(--neu-text)',
  borderRadius: 'var(--neu-radius)',
  transition: 'var(--neu-transition)',
};

// ============================================
// INPUT STYLES
// ============================================

export const neumorphismInput: CSSProperties = {
  background: 'var(--neu-bg)',
  color: 'var(--neu-text)',
  border: 'none',
  borderRadius: 'var(--neu-radius)',
  boxShadow: 'var(--neu-shadow-in)',
  padding: '12px 16px',
  fontSize: '14px',
  transition: 'var(--neu-transition)',
  outline: 'none',
};

export const neumorphismInputFocus: CSSProperties = {
  boxShadow: 'var(--neu-shadow-in-sm), 0 0 0 2px var(--neu-primary)',
};

export const neumorphismInputError: CSSProperties = {
  boxShadow: 'var(--neu-shadow-in), 0 0 0 2px var(--neu-danger)',
};

export const neumorphismInputDisabled: CSSProperties = {
  opacity: 0.6,
  cursor: 'not-allowed',
};

// ============================================
// BUTTON STYLES
// ============================================

export const neumorphismButton: CSSProperties = {
  background: 'var(--neu-bg)',
  color: 'var(--neu-text)',
  border: 'none',
  borderRadius: 'var(--neu-radius)',
  boxShadow: 'var(--neu-shadow-out)',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'var(--neu-transition)',
  outline: 'none',
};

export const neumorphismButtonHover: CSSProperties = {
  boxShadow: 'var(--neu-shadow-out-lg)',
  transform: 'translateY(-2px)',
};

export const neumorphismButtonActive: CSSProperties = {
  boxShadow: 'var(--neu-shadow-pressed)',
  transform: 'translateY(0)',
};

export const neumorphismButtonPrimary: CSSProperties = {
  background: 'var(--neu-primary)',
  color: '#ffffff',
  boxShadow: '8px 8px 16px rgba(108, 92, 231, 0.3), -8px -8px 16px var(--neu-shadow-light)',
};

export const neumorphismButtonDisabled: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
  boxShadow: 'var(--neu-shadow-out-sm)',
};

// ============================================
// CARD STYLES
// ============================================

export const neumorphismCard: CSSProperties = {
  background: 'var(--neu-bg)',
  color: 'var(--neu-text)',
  borderRadius: 'var(--neu-radius-lg)',
  boxShadow: 'var(--neu-shadow-out)',
  padding: 'var(--neu-spacing-lg)',
  transition: 'var(--neu-transition)',
};

export const neumorphismCardHover: CSSProperties = {
  boxShadow: 'var(--neu-shadow-out-lg)',
  transform: 'translateY(-4px)',
};

export const neumorphismCardInset: CSSProperties = {
  background: 'var(--neu-bg)',
  borderRadius: 'var(--neu-radius)',
  boxShadow: 'var(--neu-shadow-in)',
  padding: 'var(--neu-spacing-md)',
};

// ============================================
// SELECT STYLES
// ============================================

export const neumorphismSelect: CSSProperties = {
  background: 'var(--neu-bg)',
  color: 'var(--neu-text)',
  border: 'none',
  borderRadius: 'var(--neu-radius)',
  boxShadow: 'var(--neu-shadow-in)',
  padding: '12px 40px 12px 16px',
  fontSize: '14px',
  cursor: 'pointer',
  transition: 'var(--neu-transition)',
  appearance: 'none',
};

export const neumorphismSelectDropdown: CSSProperties = {
  background: 'var(--neu-bg)',
  borderRadius: 'var(--neu-radius)',
  boxShadow: 'var(--neu-shadow-out-lg)',
  padding: 'var(--neu-spacing-sm)',
  marginTop: '8px',
};

export const neumorphismSelectOption: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 'var(--neu-radius-sm)',
  cursor: 'pointer',
  transition: 'var(--neu-transition-fast)',
};

export const neumorphismSelectOptionHover: CSSProperties = {
  background: 'var(--neu-bg-secondary)',
  boxShadow: 'var(--neu-shadow-in-sm)',
};

// ============================================
// TABLE STYLES
// ============================================

export const neumorphismTable: CSSProperties = {
  background: 'var(--neu-bg)',
  borderRadius: 'var(--neu-radius-lg)',
  boxShadow: 'var(--neu-shadow-out)',
  overflow: 'hidden',
};

export const neumorphismTableHeader: CSSProperties = {
  background: 'var(--neu-bg-secondary)',
  boxShadow: 'var(--neu-shadow-in-sm)',
  padding: '16px',
  fontWeight: 600,
  color: 'var(--neu-text)',
};

export const neumorphismTableRow: CSSProperties = {
  borderBottom: '1px solid var(--neu-bg-secondary)',
  transition: 'var(--neu-transition-fast)',
};

export const neumorphismTableRowHover: CSSProperties = {
  background: 'var(--neu-bg-secondary)',
};

export const neumorphismTableCell: CSSProperties = {
  padding: '14px 16px',
  color: 'var(--neu-text)',
};

// ============================================
// MODAL STYLES
// ============================================

export const neumorphismModalOverlay: CSSProperties = {
  background: 'rgba(224, 229, 236, 0.8)',
  backdropFilter: 'blur(8px)',
};

export const neumorphismModal: CSSProperties = {
  background: 'var(--neu-bg)',
  borderRadius: 'var(--neu-radius-xl)',
  boxShadow: 'var(--neu-shadow-out-lg)',
  padding: 'var(--neu-spacing-xl)',
};

export const neumorphismModalHeader: CSSProperties = {
  marginBottom: 'var(--neu-spacing-lg)',
  paddingBottom: 'var(--neu-spacing-md)',
  borderBottom: '1px solid var(--neu-bg-secondary)',
};

// ============================================
// TABS STYLES
// ============================================

export const neumorphismTabs: CSSProperties = {
  background: 'var(--neu-bg)',
  borderRadius: 'var(--neu-radius)',
  boxShadow: 'var(--neu-shadow-in)',
  padding: '4px',
  display: 'flex',
  gap: '4px',
};

export const neumorphismTab: CSSProperties = {
  padding: '10px 20px',
  borderRadius: 'var(--neu-radius-sm)',
  background: 'transparent',
  color: 'var(--neu-text-secondary)',
  border: 'none',
  cursor: 'pointer',
  transition: 'var(--neu-transition)',
  fontWeight: 500,
};

export const neumorphismTabActive: CSSProperties = {
  background: 'var(--neu-bg)',
  boxShadow: 'var(--neu-shadow-out-sm)',
  color: 'var(--neu-primary)',
};

// ============================================
// BADGE STYLES
// ============================================

export const neumorphismBadge: CSSProperties = {
  background: 'var(--neu-bg)',
  color: 'var(--neu-text)',
  borderRadius: '999px',
  boxShadow: 'var(--neu-shadow-out-sm)',
  padding: '4px 12px',
  fontSize: '12px',
  fontWeight: 600,
};

export const neumorphismBadgeSuccess: CSSProperties = {
  background: 'var(--neu-success)',
  color: '#ffffff',
};

export const neumorphismBadgeWarning: CSSProperties = {
  background: 'var(--neu-warning)',
  color: '#333333',
};

export const neumorphismBadgeDanger: CSSProperties = {
  background: 'var(--neu-danger)',
  color: '#ffffff',
};

// ============================================
// TOOLTIP STYLES
// ============================================

export const neumorphismTooltip: CSSProperties = {
  background: 'var(--neu-bg)',
  color: 'var(--neu-text)',
  borderRadius: 'var(--neu-radius-sm)',
  boxShadow: 'var(--neu-shadow-out)',
  padding: '8px 12px',
  fontSize: '12px',
};
